import { useMemo, useState, useCallback } from 'react'
import type { ColumnsType } from 'antd/es/table'
import {
  // ClockCircleFilled,
  // InfoCircleFilled,
  CaretRightOutlined,
  CaretDownOutlined,
  FilterOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckOutlined
  // InboxOutlined
} from '@ant-design/icons'
import { Table, Spin, DatePicker, Select, Button, Space, message, Tooltip, Tag } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import $api from '../../../API'
import { PageContainer, PageGrid, PageHeader } from '../../../components/page'
import type { LockboxChecksResponse, LockboxDailyBreakdown } from './lockbox-types'

import './lockbox.css'

const { RangePicker } = DatePicker

type LockboxStatus = 'partial' | 'mixed'

interface LockboxCheckRow {
  id: string
  client: string
  location: string
  deposits: number
  amount: number
  accountLast4: string
  accountNumber: string
  status: LockboxStatus
  firstTransactionDate: string
  lastTransactionDate: string
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
})

const getLocationFullName = (location: string): string => {
  switch (location) {
    case 'CA':
      return 'California'
    case 'TN':
      return 'Tennessee'
    case 'WI':
      return 'Wisconsin'
    default:
      return 'Unknown'
  }
}

const getAccountLast4 = (accountNumber: string): string => {
  return accountNumber.slice(-4)
}

function TransactionsTable({
  lockboxId,
  dailyBreakdown
}: {
  lockboxId: string
  dailyBreakdown: LockboxDailyBreakdown[]
}) {
  const navigate = useNavigate()
  const filteredData = dailyBreakdown.filter(item => item.lockbox_id === lockboxId)

  const columns = [
    {
      title: 'Date',
      dataIndex: 'transaction_date',
      key: 'transaction_date',
      width: 60,
      render: (val: string) => dayjs(val).format('MM/DD/YYYY')
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount_dollars',
      key: 'total_amount_dollars',
      width: 70,
      align: 'right' as const,
      render: (val: number) => (
        <span className='font-medium text-slate-800'>{currencyFormatter.format(val)}</span>
      )
    },
    {
      title: 'Checks',
      dataIndex: 'checks_count',
      key: 'checks_count',
      width: 150,
      align: 'center' as const,
      render: (val: number) => {
        if (val === 0) {
          return <Tag color='default'>No Checks</Tag>
        }

        return <span className='font-medium text-slate-800'>{val}</span>
      }
    }
  ]

  return (
    <div className='[&_.ant-table-tbody>tr:hover>td]:!bg-slate-100'>
      <Table
        size='small'
        tableLayout='fixed'
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`
        }}
        columns={columns}
        dataSource={filteredData}
        rowKey={record => `${record.lockbox_id}-${record.transaction_date}`}
        onRow={record => ({
          onClick: () => {
            const dateStr = dayjs(record.transaction_date).format('YYYY-MM-DD')

            navigate(`/integrations/pinnacle/lockbox/${lockboxId}?date=${dateStr}`)
          }
        })}
        rowClassName='cursor-pointer'
      />
    </div>
  )
}

export function PinnacleLockboxChecksPage(): JSX.Element {
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])
  const [dateRange, setDateRange] = useState<[string, string] | null>(null)
  const [selectedLockboxes, setSelectedLockboxes] = useState<string[]>([])
  const [visibleAccountNumbers, setVisibleAccountNumbers] = useState<Set<string>>(new Set())
  const [copiedAccountId, setCopiedAccountId] = useState<string | null>(null)

  const handleCopyAccountNumber = useCallback(async (accountNumber: string, recordId: string) => {
    try {
      await navigator.clipboard.writeText(accountNumber)
      setCopiedAccountId(recordId)
      setTimeout(() => setCopiedAccountId(null), 2000)
    } catch {
      message.error('Failed to copy account number')
    }
  }, [])

  const { data, isLoading, error } = useQuery<LockboxChecksResponse>({
    queryKey: ['lockbox-checks', dateRange],
    queryFn: async () => {
      const params = dateRange ? { startDate: dateRange[0], endDate: dateRange[1] } : {}
      const { data } = await $api.get<LockboxChecksResponse>('pinnacle/lockbox/checks', {
        params
      })
      return data
    }
  })

  const lockboxRows = useMemo<LockboxCheckRow[]>(() => {
    if (!data?.summary) return []

    const allRows = data.summary.map(item => {
      const accountNumber = item.account_number
      const location =
        accountNumber === '800106613334'
          ? 'CA'
          : accountNumber === '16074120'
            ? 'CA'
            : accountNumber === '800105080006'
              ? 'TN'
              : accountNumber === '800105111728'
                ? 'WI'
                : accountNumber === '800106953649'
                  ? 'WI'
                  : 'Unknown'

      return {
        id: item.lockbox_id,
        client: item.lockbox_name,
        location: getLocationFullName(location),
        deposits: item.transaction_count,
        amount: item.total_amount_dollars,
        accountLast4: getAccountLast4(accountNumber),
        accountNumber,
        status: 'partial' as const,
        firstTransactionDate: item.first_transaction_date,
        lastTransactionDate: item.last_transaction_date
      }
    })

    let filteredRows = allRows
    if (selectedLockboxes.length > 0) {
      filteredRows = allRows.filter(row => selectedLockboxes.includes(row.id))
    }

    return filteredRows.sort((a, b) => {
      if (a.id === '306355') return -1
      if (b.id === '306355') return 1

      if (a.location === 'California' && b.location !== 'California') return -1
      if (b.location === 'California' && a.location !== 'California') return 1
      if (a.location === 'Wisconsin' && b.location !== 'Wisconsin') return -1
      if (b.location === 'Wisconsin' && a.location !== 'Wisconsin') return 1

      return 0
    })
  }, [data, selectedLockboxes])

  const lockboxOptions = useMemo(() => {
    if (!data?.summary) return []
    return data.summary.map(item => ({
      label: `${item.lockbox_id}`,
      value: item.lockbox_id
    }))
  }, [data])

  const columns = useMemo<ColumnsType<LockboxCheckRow>>(
    () => [
      {
        key: 'account',
        title: 'Account',
        dataIndex: 'accountLast4',
        align: 'left',
        width: 180,
        render: (_value, record) => {
          const isVisible = visibleAccountNumbers.has(record.id)
          const displayValue = isVisible ? record.accountNumber : `****${record.accountLast4}`

          return (
            <div className='flex gap-2 items-center'>
              <Tooltip
                title={
                  copiedAccountId === record.id ? (
                    <span>
                      <CheckOutlined /> Copied
                    </span>
                  ) : isVisible ? (
                    'Click to copy'
                  ) : (
                    ''
                  )
                }
              >
                <span
                  className={`text-lg font-semibold text-slate-700 ${
                    isVisible ? 'cursor-pointer hover:text-blue-600 transition-colors' : ''
                  }`}
                  onClick={e => {
                    if (isVisible) {
                      e.stopPropagation()
                      handleCopyAccountNumber(record.accountNumber, record.id)
                    }
                  }}
                >
                  {displayValue}
                </span>
              </Tooltip>
              <button
                type='button'
                onClick={e => {
                  e.stopPropagation()
                  setVisibleAccountNumbers(prev => {
                    const next = new Set(prev)

                    if (isVisible) {
                      next.delete(record.id)
                    } else {
                      next.add(record.id)
                    }

                    return next
                  })
                }}
                className='flex justify-center items-center transition-colors text-slate-400 hover:text-slate-700'
              >
                {isVisible ? (
                  <EyeInvisibleOutlined className='text-base' />
                ) : (
                  <EyeOutlined className='text-base' />
                )}
              </button>
            </div>
          )
        }
      },
      {
        key: 'location',
        title: 'Location',
        dataIndex: 'location',
        align: 'left',
        width: 120,
        render: (_value, record) => {
          const tagColor =
            record.location === 'California'
              ? 'green'
              : record.location === 'Wisconsin'
                ? 'blue'
                : record.location === 'Tennessee'
                  ? 'red'
                  : 'default'

          return <Tag color={tagColor}>{record.location}</Tag>
        }
      },
      {
        key: 'dateRange',
        title: 'Available Period',
        align: 'center',
        width: 220,
        render: (_value, record) => (
          <span className='text-base text-slate-700'>
            {dayjs(record.firstTransactionDate).format('MM/DD/YYYY')} –{' '}
            {dayjs(record.lastTransactionDate).format('MM/DD/YYYY')}
          </span>
        )
      },
      {
        key: 'totalAmount',
        title: 'Total Amount',
        dataIndex: 'amount',
        align: 'right',
        width: 140,
        render: (_value, record) => (
          <span className='text-lg font-semibold text-slate-700'>
            {currencyFormatter.format(record.amount)}
          </span>
        )
      },
      {
        key: 'lockbox',
        title: 'Lockbox',
        dataIndex: 'id',
        align: 'right',
        width: 280,
        render: (_value, record) => (
          <div className='flex gap-4 items-center justify-end'>
            <span className='text-sm text-slate-400'>{record.client}</span>
            <span className='text-lg font-semibold text-slate-700'>#{record.id}</span>
          </div>
        )
      }
    ],
    [visibleAccountNumbers, copiedAccountId, handleCopyAccountNumber]
  )

  if (isLoading) {
    return (
      <PageContainer>
        <div className='flex justify-center items-center h-64'>
          <Spin size='large' />
        </div>
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <div className='text-red-600'>Error loading lockbox data</div>
      </PageContainer>
    )
  }

  const handleDateRangeChange = (dates: null | [dayjs.Dayjs | null, dayjs.Dayjs | null]) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')])
    } else {
      setDateRange(null)
    }
  }

  const handleClearFilters = () => {
    setDateRange(null)
    setSelectedLockboxes([])
  }

  return (
    <PageContainer className='lockbox-page'>
      <PageGrid<LockboxCheckRow>
        contentClassName='border border-slate-200 rounded-2xl overflow-hidden'
        toolbar={
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4'>
              <PageHeader
                breadcrumbs={[
                  { key: 'pinnacle', label: 'Pinnacle' },
                  { key: 'lockbox', label: 'Lockbox Checks' }
                ]}
              />
              <Space wrap>
                <RangePicker
                  placeholder={['Start Date', 'End Date']}
                  value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
                  onChange={handleDateRangeChange}
                  format='YYYY-MM-DD'
                />
                <Select
                  mode='multiple'
                  placeholder='Select Lockbox'
                  value={selectedLockboxes}
                  onChange={setSelectedLockboxes}
                  options={lockboxOptions}
                  style={{ minWidth: 200, maxWidth: 300 }}
                  allowClear
                  maxTagCount='responsive'
                />
                {(dateRange || selectedLockboxes.length > 0) && (
                  <Button onClick={handleClearFilters} icon={<FilterOutlined />}>
                    Clear Filters
                  </Button>
                )}
              </Space>
            </div>
            <div className='flex flex-col pt-2 pb-4 border-t border-zinc-200'>
              <span className='text-lg font-semibold text-slate-700'>Bank Transactions</span>
              <span className='text-sm text-slate-500'>
                Lockbox deposits and reconciliation status
              </span>
            </div>
          </div>
        }
        columns={columns}
        dataSource={lockboxRows}
        rowKey='id'
        expandedRowKeys={expandedRowKeys}
        onExpand={(expanded, record) => {
          if (expanded) {
            setExpandedRowKeys([...expandedRowKeys, record.id])
          } else {
            setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.id))
          }
        }}
        onRow={record => ({
          onClick: () => {
            const isExpanded = expandedRowKeys.includes(record.id)
            if (isExpanded) {
              setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.id))
            } else {
              setExpandedRowKeys([...expandedRowKeys, record.id])
            }
          }
        })}
        rowClassName={record =>
          expandedRowKeys.includes(record.id)
            ? 'bg-slate-100 border-b-2 border-slate-300'
            : 'border-b-2 border-slate-300 hover:bg-slate-50 transition-colors'
        }
        locale={{ emptyText: 'No lockbox transactions found' }}
        expandable={{
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <CaretDownOutlined
                className='cursor-pointer text-slate-500'
                onClick={e => onExpand(record, e)}
              />
            ) : (
              <CaretRightOutlined
                className='cursor-pointer text-slate-500'
                onClick={e => onExpand(record, e)}
              />
            ),
          expandedRowRender: record => (
            <div className='py-3 px-10 rounded-md bg-white'>
              <TransactionsTable
                lockboxId={record.id}
                dailyBreakdown={data?.dailyBreakdown || []}
              />
            </div>
          )
        }}
      />
    </PageContainer>
  )
}
